import { useState, useEffect, useCallback } from 'react';
import {
  fetchProperties, fetchPropertyPayments,
  markPaymentReceived, revertPaymentToProjected,
  fetchPaymentAbonos, createPaymentAbono, updatePaymentAbono, deletePaymentAbono,
  fetchCollectionAlerts, sendPaymentReminder, sendNotification, fetchNotificationPreview, fetchNotificationHistory, generateAIMessage,
  fetchReminderQueue, fetchQueueSummary, generateReminders, approveQueueItem, sendQueueItem, discardQueueItem,
} from '@/lib/api';
import { formatMXN, formatMXNCompact, formatDate } from '@/lib/formatters';
import { useAuth } from '@/lib/AuthContext';
import { PageHeader } from '@/components/common/PageHeader';
import { KPICard } from '@/components/common/KPICard';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import {
  DollarSign, Plus, Pencil, Trash2, Clock, CheckCircle2, AlertTriangle,
  Ban, Calendar, Check, X as XIcon, Bell, Send, Sparkles,
  ArrowUpDown, ArrowUp, ArrowDown,
} from 'lucide-react';

const PAY_TYPES = [
  { value: 'anticipo', label: 'Anticipo' },
  { value: 'pago_intermedio', label: 'Pago Intermedio' },
  { value: 'finiquito', label: 'Finiquito' },
];
const typeLabel = { anticipo: 'Anticipo', pago_intermedio: 'Intermedio', intermedio: 'Intermedio', finiquito: 'Finiquito' };
const typeBadge = {
  anticipo: 'bg-blue-50 text-blue-700 border-blue-200',
  pago_intermedio: 'bg-violet-50 text-violet-700 border-violet-200',
  intermedio: 'bg-violet-50 text-violet-700 border-violet-200',
  finiquito: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};
const statusConfig = {
  projected: { cls: 'bg-slate-100 text-slate-600 border-slate-300', icon: Clock, label: 'Programado' },
  partial:   { cls: 'bg-amber-50 text-amber-700 border-amber-300', icon: Clock, label: 'Parcial' },
  received:  { cls: 'bg-emerald-50 text-emerald-700 border-emerald-300', icon: CheckCircle2, label: 'Recibido' },
  overdue:   { cls: 'bg-red-50 text-red-600 border-red-300', icon: AlertTriangle, label: 'Vencido' },
  cancelled: { cls: 'bg-slate-50 text-slate-400 border-slate-200', icon: Ban, label: 'Cancelado' },
};
const MES_NAMES = ['','Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];


export default function Pagos() {
  const { can } = useAuth();
  const canEdit = can('pagos', 'edit');
  const [payments, setPayments] = useState([]);
  const [props, setProps] = useState({});
  const [alerts, setAlerts] = useState({ payment_alerts: [], property_alerts: [], summary: {} });
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterAlert, setFilterAlert] = useState('all');
  const [editTarget, setEditTarget] = useState(null);
  const [reminderTarget, setReminderTarget] = useState(null);
  const [sortCol, setSortCol] = useState(null); // 'vivienda'|'cliente'|'scheduled_date'|'actual_date'
  const [sortDir, setSortDir] = useState(null); // 'asc'|'desc'|null

  const load = useCallback(async () => {
    try {
      const [pays, properties, alertData] = await Promise.all([
        fetchPropertyPayments(), fetchProperties(), fetchCollectionAlerts(),
      ]);
      const pMap = {};
      for (const p of properties) pMap[p.id] = p;
      setProps(pMap);
      // Build lookups
      const alertByPay = {};
      for (const pa of alertData.payment_alerts) alertByPay[pa.payment_id] = pa;
      const enriched = pays.map(pp => ({
        ...pp,
        prop_name: pMap[pp.property_id]?.name || '',
        prop_block: pMap[pp.property_id]?.block || '',
        prop_lot: pMap[pp.property_id]?.lot || '',
        prop_modelo: pMap[pp.property_id]?.modelo || '',
        prop_deed: pMap[pp.property_id]?.deed_date || '',
        _alerts: alertByPay[pp.id]?.alerts || [],
        _severity: alertByPay[pp.id]?.max_severity || null,
      }));
      setPayments(enriched);
      setAlerts(alertData);
    } catch { toast.error('Error al cargar pagos'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const today = new Date().toISOString().slice(0, 10);

  // Filter
  const filtered = payments.filter(pp => {
    if (filterStatus !== 'all' && pp.payment_status !== filterStatus) return false;
    if (filterType !== 'all' && pp.payment_type !== filterType) return false;
    if (filterAlert !== 'all') {
      if (filterAlert === 'any_alert') return pp._alerts.length > 0;
      return pp._alerts.some(a => a.type === filterAlert);
    }
    return true;
  });

  // Sort handler
  const toggleSort = (col) => {
    if (sortCol === col) {
      if (sortDir === 'asc') setSortDir('desc');
      else if (sortDir === 'desc') { setSortCol(null); setSortDir(null); }
    } else {
      setSortCol(col); setSortDir('asc');
    }
  };

  // Apply sort
  const sorted = (() => {
    let items = [...filtered];
    if (!sortCol || !sortDir) {
      // Priority: overdue & partial first (by scheduled_date asc), then projected (by scheduled_date asc), then received (by actual_date desc)
      const statusPriority = { overdue: 0, partial: 0, projected: 1, received: 2, cancelled: 3 };
      return items.sort((a, b) => {
        const ap = statusPriority[a.payment_status] ?? 2;
        const bp = statusPriority[b.payment_status] ?? 2;
        if (ap !== bp) return ap - bp;
        // Within same priority: sort by scheduled_date asc
        if (ap <= 1) return (a.scheduled_date || '9').localeCompare(b.scheduled_date || '9');
        // Received: by actual_date desc
        return (b.actual_date || '').localeCompare(a.actual_date || '');
      });
    }
    const dir = sortDir === 'asc' ? 1 : -1;
    items.sort((a, b) => {
      let va, vb;
      switch (sortCol) {
        case 'vivienda':
          va = `${a.prop_lot || ''}-${a.prop_block || ''}`.toLowerCase();
          vb = `${b.prop_lot || ''}-${b.prop_block || ''}`.toLowerCase();
          return va.localeCompare(vb) * dir;
        case 'cliente':
          va = (a.prop_name || '').toLowerCase();
          vb = (b.prop_name || '').toLowerCase();
          return va.localeCompare(vb) * dir;
        case 'scheduled_date':
          va = a.scheduled_date || '';
          vb = b.scheduled_date || '';
          return va.localeCompare(vb) * dir;
        case 'actual_date':
          // Nulls: asc → nulls first, desc → nulls last
          va = a.actual_date || null;
          vb = b.actual_date || null;
          if (!va && !vb) return 0;
          if (!va) return dir === 1 ? -1 : 1;
          if (!vb) return dir === 1 ? 1 : -1;
          return va.localeCompare(vb) * dir;
        default:
          return 0;
      }
    });
    return items;
  })();

  // KPIs
  const active = payments.filter(p => p.payment_status !== 'cancelled');
  const totalScheduled = active.reduce((s, p) => s + (p.scheduled_amount || 0), 0);
  const totalReceived = active.filter(p => p.payment_status === 'received' || p.payment_status === 'partial')
    .reduce((s, p) => s + (p.actual_amount || 0), 0);
  const totalPending = totalScheduled - totalReceived;
  const overdueCount = active.filter(p => p.payment_status === 'overdue').length;
  const overdueAmount = active.filter(p => p.payment_status === 'overdue')
    .reduce((s, p) => s + (p.scheduled_amount || 0), 0);
  // Month-specific metrics
  const currentMonth = today.slice(0, 7);
  const monthActive = active.filter(p => (p.scheduled_date || '').startsWith(currentMonth));
  const monthScheduled = monthActive.reduce((s, p) => s + (p.scheduled_amount || 0), 0);
  const monthReceived = monthActive.filter(p => p.payment_status === 'received' || p.payment_status === 'partial')
    .reduce((s, p) => s + (p.actual_amount || 0), 0);
  const monthCompliance = monthScheduled > 0 ? (monthReceived / monthScheduled * 100) : 0;
  const upcomingPays = active.filter(p => p.days_until_due != null && p.days_until_due <= 15 && p.days_until_due > 0);
  const upcomingAmount = upcomingPays.reduce((s, p) => s + (p.scheduled_amount || 0), 0);
  const as = alerts.summary || {};

  // Calendar data
  const byMonth = {};
  for (const pp of active) {
    const m = (pp.scheduled_date || '').slice(0, 7);
    if (!m) continue;
    byMonth[m] = byMonth[m] || { scheduled: 0, received: 0, items: [] };
    byMonth[m].scheduled += pp.scheduled_amount || 0;
    if (pp.payment_status === 'received' || pp.payment_status === 'partial')
      byMonth[m].received += pp.actual_amount || 0;
    byMonth[m].items.push(pp);
  }

  // Reminder handler
  const handleRemind = (pp) => {
    setReminderTarget(pp);
  };

  // Additional metrics
  const finiquitosPending = active.filter(p => p.payment_type === 'finiquito' && p.payment_status !== 'received');
  const finiquitosAtRisk = alerts.payment_alerts?.filter(pa => pa.alerts?.some(a => a.type === 'closing_risk')) || [];
  const remindersThisMonth = active.filter(p => p.last_reminder_sent_at && p.last_reminder_sent_at.startsWith(currentMonth)).length;
  const criticalPayments = [...active].filter(p => p.payment_status === 'overdue').sort((a, b) => (b.days_overdue || 0) - (a.days_overdue || 0)).slice(0, 5);
  const topReminded = [...active].filter(p => (p.reminder_count || 0) > 0).sort((a, b) => (b.reminder_count || 0) - (a.reminder_count || 0)).slice(0, 5);

  if (loading) return (
    <div className="space-y-6" data-testid="pagos-loading">
      <Skeleton className="h-10 w-48" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">{[1,2,3,4].map(i => <Skeleton key={i} className="h-28 rounded-xl" />)}</div>
      <Skeleton className="h-[500px] rounded-xl" />
    </div>
  );

  const MES_NAMES_SHORT = ['','Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
  const monthLabel = `${MES_NAMES_SHORT[parseInt(currentMonth.slice(5))] || '?'} ${currentMonth.slice(0, 4)}`;

  return (
    <div className="space-y-4" data-testid="pagos-page">
        <PageHeader title="Pagos" subtitle={`${active.length} pagos · ${monthLabel}`} />

      {/* ── ROW 1: 5 KPI cards ── */}
      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-3">
        <Card className="rounded-xl border bg-white border-emerald-200"><CardContent className="p-3.5">
          <div className="flex items-center justify-between mb-1"><span className="text-[9px] uppercase tracking-widest text-slate-400">Cobrado Mes</span><div className="h-2.5 w-2.5 rounded-full bg-emerald-500" /></div>
          <p className="text-[17px] font-bold tabular-nums text-emerald-700" style={{ fontFamily: 'Space Grotesk' }}>{formatMXNCompact(monthReceived)}</p>
          <p className="text-[10px] text-slate-400 mt-0.5">{monthLabel}</p>
        </CardContent></Card>
        <Card className="rounded-xl border bg-white border-red-200"><CardContent className="p-3.5">
          <div className="flex items-center justify-between mb-1"><span className="text-[9px] uppercase tracking-widest text-slate-400">Vencido Total</span><div className="h-2.5 w-2.5 rounded-full bg-red-500" /></div>
          <p className="text-[17px] font-bold tabular-nums text-red-600" style={{ fontFamily: 'Space Grotesk' }}>{formatMXNCompact(overdueAmount)}</p>
          <p className="text-[10px] text-red-400 mt-0.5">{overdueCount} pago{overdueCount !== 1 ? 's' : ''}</p>
        </CardContent></Card>
        <Card className="rounded-xl border bg-white border-amber-200"><CardContent className="p-3.5">
          <div className="flex items-center justify-between mb-1"><span className="text-[9px] uppercase tracking-widest text-slate-400">Por Vencer</span><div className="h-2.5 w-2.5 rounded-full bg-amber-400" /></div>
          <p className="text-[17px] font-bold tabular-nums text-amber-700" style={{ fontFamily: 'Space Grotesk' }}>{formatMXNCompact(upcomingAmount)}</p>
          <p className="text-[10px] text-amber-400 mt-0.5">{upcomingPays.length} próximo{upcomingPays.length !== 1 ? 's' : ''}</p>
        </CardContent></Card>
        <Card className="rounded-xl border bg-white"><CardContent className="p-3.5">
          <div className="flex items-center justify-between mb-1"><span className="text-[9px] uppercase tracking-widest text-slate-400">Saldo por Cobrar</span></div>
          <p className="text-[17px] font-bold tabular-nums text-slate-700" style={{ fontFamily: 'Space Grotesk' }}>{formatMXNCompact(totalPending)}</p>
          <p className="text-[10px] text-slate-400 mt-0.5">{finiquitosPending.length} finiquito{finiquitosPending.length !== 1 ? 's' : ''} pend.</p>
        </CardContent></Card>
        <Card className={`rounded-xl border bg-white ${monthCompliance >= 80 ? 'border-emerald-200' : monthCompliance >= 50 ? 'border-amber-200' : 'border-red-200'}`}><CardContent className="p-3.5">
          <div className="flex items-center justify-between mb-1"><span className="text-[9px] uppercase tracking-widest text-slate-400">Cumplimiento</span></div>
          <p className={`text-[17px] font-bold tabular-nums ${monthCompliance >= 80 ? 'text-emerald-600' : monthCompliance >= 50 ? 'text-amber-600' : 'text-red-500'}`} style={{ fontFamily: 'Space Grotesk' }}>{monthCompliance.toFixed(0)}%</p>
          <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden mt-1.5"><div className={`h-full rounded-full ${monthCompliance >= 80 ? 'bg-emerald-500' : monthCompliance >= 50 ? 'bg-amber-400' : 'bg-red-400'}`} style={{ width: `${Math.min(monthCompliance, 100)}%` }} /></div>
        </CardContent></Card>
      </div>

      {/* ── ROW 2: Cumplimiento bar + Listas prioritarias ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Cumplimiento detallado */}
        <Card className="rounded-xl border bg-white" data-testid="cumplimiento-detail">
          <CardContent className="p-4">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-3">Resumen {monthLabel}</p>
            <div className="space-y-2.5 text-xs">
              <div className="flex justify-between"><span className="text-slate-500">Programado del mes</span><span className="font-bold tabular-nums">{formatMXNCompact(monthScheduled)}</span></div>
              <div className="flex justify-between"><span className="text-emerald-600">Cobrado real del mes</span><span className="font-bold tabular-nums text-emerald-700">{formatMXNCompact(monthReceived)}</span></div>
              <div className="h-px bg-slate-100" />
              <div className="flex justify-between"><span className="text-slate-500">Programado total</span><span className="tabular-nums">{formatMXNCompact(totalScheduled)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Cobrado total</span><span className="tabular-nums text-emerald-700">{formatMXNCompact(totalReceived)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Recordatorios este mes</span><span className="font-semibold text-[#F05A28]">{remindersThisMonth}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Finiquitos en riesgo</span><span className="font-semibold text-red-500">{finiquitosAtRisk.length}</span></div>
            </div>
          </CardContent>
        </Card>

        {/* Pagos críticos */}
        <Card className="rounded-xl border bg-white" data-testid="critical-payments">
          <CardContent className="p-4">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-red-400 mb-3">Pagos Críticos</p>
            {criticalPayments.length === 0 ? (
              <p className="text-xs text-slate-400 text-center py-4">Sin pagos vencidos</p>
            ) : (
              <div className="space-y-2">
                {criticalPayments.map(pp => (
                  <div key={pp.id} className="flex items-center gap-2 text-[11px]">
                    <div className="h-2 w-2 rounded-full bg-red-500 shrink-0" />
                    <span className="text-slate-600 truncate flex-1">{pp.prop_name || `Lt${pp.prop_lot} Mz${pp.prop_block}`}</span>
                    <span className="tabular-nums font-bold text-red-600">{formatMXNCompact(pp.scheduled_amount)}</span>
                    <span className="text-[9px] text-red-400">{pp.days_overdue}d</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Finiquitos en riesgo */}
        <Card className="rounded-xl border bg-white" data-testid="finiquitos-risk">
          <CardContent className="p-4">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-amber-500 mb-3">Finiquitos Pendientes</p>
            {finiquitosPending.length === 0 ? (
              <p className="text-xs text-slate-400 text-center py-4">Sin finiquitos pendientes</p>
            ) : (
              <div className="space-y-2">
                {finiquitosPending.slice(0, 5).map(pp => {
                  const isRisk = finiquitosAtRisk.some(r => r.payment_id === pp.id);
                  return (
                    <div key={pp.id} className="flex items-center gap-2 text-[11px]">
                      <div className={`h-2 w-2 rounded-full shrink-0 ${isRisk ? 'bg-red-500' : 'bg-amber-400'}`} />
                      <span className="text-slate-600 truncate flex-1">{pp.prop_name || `Lt${pp.prop_lot} Mz${pp.prop_block}`}</span>
                      <span className="tabular-nums font-bold">{formatMXNCompact(pp.scheduled_amount)}</span>
                      {isRisk && <Badge variant="outline" className="text-[7px] bg-red-50 text-red-600 border-red-200 px-1">Riesgo</Badge>}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ── Tabs zone ── */}
      <Tabs defaultValue="table" className="space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <TabsList>
            <TabsTrigger value="table">Tabla</TabsTrigger>
            <TabsTrigger value="risk">
              Cobranza
              {as.collection_risk > 0 && <Badge className="ml-1.5 h-4 px-1.5 text-[9px] bg-red-500 text-white">{as.collection_risk}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="queue">Cola</TabsTrigger>
            <TabsTrigger value="calendar">Calendario</TabsTrigger>
          </TabsList>
          <div className="flex items-center gap-2">
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[150px] h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los estatus</SelectItem>
                <SelectItem value="projected">Programado</SelectItem>
                <SelectItem value="partial">Parcial</SelectItem>
                <SelectItem value="received">Recibido</SelectItem>
                <SelectItem value="overdue">Vencido</SelectItem>
                <SelectItem value="cancelled">Cancelado</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-[140px] h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los tipos</SelectItem>
                {PAY_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterAlert} onValueChange={setFilterAlert}>
              <SelectTrigger className={`w-[150px] h-8 text-xs ${filterAlert !== 'all' ? 'border-red-300 text-red-600' : ''}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Sin filtro alerta</SelectItem>
                <SelectItem value="any_alert">Con alerta</SelectItem>
                <SelectItem value="upcoming">Próximos a vencer</SelectItem>
                <SelectItem value="overdue">Vencidos</SelectItem>
                <SelectItem value="short_paid">Cobro inferior</SelectItem>
                <SelectItem value="closing_risk">Riesgo escritura</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* ── TAB: TABLE ── */}
        <TabsContent value="table">
          <Card className="rounded-xl border bg-white overflow-clip">
              <Table data-testid="pagos-table">
                <TableHeader className="sticky top-0 z-20">
                  <TableRow className="bg-slate-50 shadow-[0_1px_0_0_rgb(226,232,240)]">
                    <SortableHead col="vivienda" label="Vivienda" sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} />
                    <SortableHead col="cliente" label="Cliente" sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} />
                    <TableHead className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold py-2.5 px-2.5">Tipo</TableHead>
                    <TableHead className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold py-2.5 px-2.5">Estatus</TableHead>
                    <SortableHead col="scheduled_date" label="F. Programada" sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} />
                    <TableHead className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold py-2.5 px-2.5 text-right">Monto Prog.</TableHead>
                    <SortableHead col="actual_date" label="F. Real" sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} accent />
                    <TableHead className="text-[9px] uppercase tracking-widest text-[#F05A28] font-semibold py-2.5 px-2.5 text-right">Monto Real</TableHead>
                    <TableHead className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold py-2.5 px-2.5 text-right">Dif.</TableHead>
                    <TableHead className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold py-2.5 px-2.5 text-center w-16">Atraso</TableHead>
                    <TableHead className="text-[9px] uppercase tracking-widest text-red-400 font-semibold py-2.5 px-2.5 w-28">Alerta</TableHead>
                    <TableHead className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold py-2.5 px-2.5 text-center w-14">Recor.</TableHead>
                    <TableHead className="w-32" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sorted.map(pp => {
                    const st = statusConfig[pp.payment_status] || statusConfig.projected;
                    const StIcon = st.icon;
                    const diff = (pp.payment_status === 'received' || pp.payment_status === 'partial') && pp.actual_amount != null ? pp.actual_amount - (pp.scheduled_amount || 0) : null;
                    const daysLate = pp.payment_status === 'overdue' && pp.scheduled_date
                      ? Math.max(0, Math.floor((new Date(today) - new Date(pp.scheduled_date)) / 86400000))
                      : null;
                    const isReceived = pp.payment_status === 'received';
                    const isPartial = pp.payment_status === 'partial';
                    const isCancelled = pp.payment_status === 'cancelled';

                    return (
                      <TableRow key={pp.id} className={`group ${pp.payment_status === 'overdue' ? 'bg-red-50/30' : isCancelled ? 'opacity-35' : ''}`} data-testid={`pay-row-${pp.id}`}>
                        <TableCell className="py-2 px-2.5 text-[11px] font-semibold whitespace-nowrap">Lt {pp.prop_lot} Mz {pp.prop_block}</TableCell>
                        <TableCell className="py-2 px-2.5 text-[11px] text-slate-600 truncate max-w-[140px]">{pp.prop_name || '—'}</TableCell>
                        <TableCell className="py-2 px-2.5"><Badge variant="outline" className={`${typeBadge[pp.payment_type] || ''} text-[9px]`}>{typeLabel[pp.payment_type] || pp.payment_type}</Badge></TableCell>
                        <TableCell className="py-2 px-2.5"><Badge variant="outline" className={`${st.cls} text-[9px] gap-1`}><StIcon className="h-2.5 w-2.5" />{st.label}</Badge></TableCell>
                        <TableCell className="py-2 px-2.5 text-[11px] tabular-nums">{formatDate(pp.scheduled_date)}</TableCell>
                        <TableCell className="py-2 px-2.5 text-right text-[11px] tabular-nums">{formatMXN(pp.scheduled_amount)}</TableCell>
                        <TableCell className="py-2 px-2.5 text-[11px] tabular-nums text-[#F05A28] font-medium">{pp.actual_date ? formatDate(pp.actual_date) : <span className="text-slate-300">—</span>}</TableCell>
                        <TableCell className="py-2 px-2.5 text-right text-[11px] tabular-nums font-bold text-[#F05A28]">{pp.actual_amount != null ? formatMXN(pp.actual_amount) : <span className="text-slate-300">—</span>}</TableCell>
                        <TableCell className="py-2 px-2.5 text-right">
                          {diff != null ? <span className={`text-[10px] tabular-nums font-semibold ${diff > 0 ? 'text-emerald-600' : diff < 0 ? 'text-red-500' : 'text-slate-400'}`}>{diff === 0 ? '—' : formatMXN(diff)}</span> : <span className="text-slate-300 text-[10px]">—</span>}
                        </TableCell>
                        <TableCell className="py-2 px-2.5 text-center">
                          {daysLate != null ? <span className="text-[10px] font-bold text-red-500">{daysLate}d</span> : <span className="text-slate-300 text-[10px]">—</span>}
                        </TableCell>
                        <TableCell className="py-2 px-2.5">
                          {pp._alerts.length > 0 && (
                            <div className="flex flex-wrap gap-0.5">
                              {pp._alerts.map((a, i) => (
                                <span key={i} className={`inline-block text-[8px] font-semibold px-1.5 py-0.5 rounded-full ${a.severity === 'critical' ? 'bg-red-100 text-red-700' : a.severity === 'danger' ? 'bg-red-50 text-red-600' : 'bg-amber-50 text-amber-700'}`}>
                                  {a.type === 'upcoming' ? 'Próximo' : a.type === 'overdue' ? 'Vencido' : a.type === 'short_paid' ? 'Parcial' : a.type === 'closing_risk' ? 'Escritura' : a.type}
                                </span>
                              ))}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="py-2 px-2.5 text-center">
                          {pp.reminder_count > 0 ? (
                            <span className="inline-flex items-center gap-0.5 text-[10px] text-[#F05A28] font-semibold" title={`Último: ${pp.last_reminder_sent_at || '—'}`}>
                              <Bell className="h-2.5 w-2.5" />{pp.reminder_count}
                            </span>
                          ) : <span className="text-slate-300 text-[10px]">—</span>}
                        </TableCell>
                        <TableCell className="py-2 px-2.5">
                          <div className="flex items-center gap-1">
                            {!isCancelled && canEdit && (isReceived ? (
                              <Button size="sm" variant="outline" className="h-6 px-2 text-[10px]" onClick={() => setEditTarget(pp)}><Pencil className="h-3 w-3 mr-1" /> Editar</Button>
                            ) : isPartial ? (
                              <Button size="sm" className="h-6 px-2 text-[10px] bg-amber-600 hover:bg-amber-700" onClick={() => setEditTarget(pp)}><DollarSign className="h-3 w-3 mr-1" /> Abonar</Button>
                            ) : (
                              <Button size="sm" className="h-6 px-2 text-[10px] bg-emerald-600 hover:bg-emerald-700" onClick={() => setEditTarget(pp)}><CheckCircle2 className="h-3 w-3 mr-1" /> Registrar</Button>
                            ))}
                            {canEdit && (pp.payment_status === 'overdue' || pp.payment_status === 'projected' || pp.payment_status === 'partial') && (
                              <Button size="sm" variant="ghost" className="h-6 px-1.5 text-[10px] text-slate-400 hover:text-[#F05A28]" onClick={() => handleRemind(pp)} title="Enviar recordatorio">
                                <Bell className="h-3 w-3" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
          </Card>
        </TabsContent>

        {/* ── TAB: COBRANZA EN RIESGO ── */}
        <TabsContent value="risk">
          {alerts.property_alerts.length === 0 ? (
            <Card className="rounded-xl border bg-white"><CardContent className="py-12 text-center"><CheckCircle2 className="h-12 w-12 text-emerald-300 mx-auto mb-3" /><p className="text-sm font-medium text-slate-600">Sin alertas de cobranza</p></CardContent></Card>
          ) : (
            <Card className="rounded-xl border bg-white overflow-hidden">
              <Table data-testid="cobranza-table">
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold py-2.5 px-3">Vivienda</TableHead>
                    <TableHead className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold py-2.5 px-3">Cliente</TableHead>
                    <TableHead className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold py-2.5 px-3">Escritura</TableHead>
                    <TableHead className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold py-2.5 px-3 text-right">Programado</TableHead>
                    <TableHead className="text-[9px] uppercase tracking-widest text-[#F05A28] font-semibold py-2.5 px-3 text-right">Recibido</TableHead>
                    <TableHead className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold py-2.5 px-3 text-right">% Cobrado</TableHead>
                    <TableHead className="text-[9px] uppercase tracking-widest text-red-400 font-semibold py-2.5 px-3 text-center">Vencidos</TableHead>
                    <TableHead className="text-[9px] uppercase tracking-widest text-red-400 font-semibold py-2.5 px-3">Alertas</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {alerts.property_alerts.map(pr => (
                    <TableRow key={pr.property_id} className={`${pr.max_severity === 'critical' ? 'bg-red-50/40' : ''}`}>
                      <TableCell className="py-2.5 px-3 text-[12px] font-semibold">Lt {pr.lot} Mz {pr.block}</TableCell>
                      <TableCell className="py-2.5 px-3 text-[11px] text-slate-600 truncate max-w-[140px]">{pr.name || '—'}</TableCell>
                      <TableCell className="py-2.5 px-3 text-[11px] tabular-nums">{pr.deed_date ? formatDate(pr.deed_date) : '—'}</TableCell>
                      <TableCell className="py-2.5 px-3 text-right text-[11px] tabular-nums">{formatMXN(pr.total_scheduled)}</TableCell>
                      <TableCell className="py-2.5 px-3 text-right text-[11px] tabular-nums font-bold text-[#F05A28]">{formatMXN(pr.total_received)}</TableCell>
                      <TableCell className="py-2.5 px-3 text-right text-[11px] tabular-nums font-semibold">{pr.pct_cobrado}%</TableCell>
                      <TableCell className="py-2.5 px-3 text-center">{pr.overdue_count > 0 ? <span className="text-[11px] font-bold text-red-500">{pr.overdue_count}</span> : '—'}</TableCell>
                      <TableCell className="py-2.5 px-3">
                        <div className="flex flex-wrap gap-0.5">
                          {pr.alerts.map((a, i) => (
                            <span key={i} className={`text-[8px] font-semibold px-1.5 py-0.5 rounded-full ${a.severity === 'critical' ? 'bg-red-100 text-red-700' : a.severity === 'danger' ? 'bg-red-50 text-red-600' : 'bg-amber-50 text-amber-700'}`}>{a.label}</span>
                          ))}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
        </TabsContent>

        {/* ── TAB: QUEUE ── */}
        <TabsContent value="queue">
          <ReminderQueueView />
        </TabsContent>

        {/* ── TAB: CALENDAR ── */}
        <TabsContent value="calendar">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {Object.entries(byMonth).sort(([a],[b]) => a.localeCompare(b)).map(([m, data]) => {
              const [y, mo] = m.split('-');
              const pct = data.scheduled > 0 ? (data.received / data.scheduled * 100) : 0;
              const isPast = m < today.slice(0, 7);
              const isCurrent = m === today.slice(0, 7);
              return (
                <Card key={m} className={`rounded-xl border bg-white ${isCurrent ? 'border-[#F05A28] ring-1 ring-[#F05A28]/20' : ''}`} data-testid={`cal-month-${m}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-sm font-bold text-slate-800" style={{ fontFamily: 'Space Grotesk' }}>{MES_NAMES[parseInt(mo)]} {y}</p>
                      <Badge variant="outline" className="text-[9px] text-slate-400 border-slate-200">{data.items.length} pago{data.items.length !== 1 ? 's' : ''}</Badge>
                    </div>
                    {/* Progress bar */}
                    <div className="space-y-1 mb-3">
                      <div className="flex items-center gap-2">
                        <span className="text-[9px] uppercase text-slate-400 w-14 shrink-0">Programado</span>
                        <div className="flex-1 h-2 rounded-full bg-slate-100"><div className="h-full rounded-full bg-slate-400" style={{ width: '100%' }} /></div>
                        <span className="text-[10px] tabular-nums text-slate-600 w-20 text-right">{formatMXNCompact(data.scheduled)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[9px] uppercase text-[#F05A28] font-medium w-14 shrink-0">Recibido</span>
                        <div className="flex-1 h-2 rounded-full bg-slate-100"><div className="h-full rounded-full bg-[#F05A28]" style={{ width: `${Math.min(pct, 100)}%` }} /></div>
                        <span className="text-[10px] tabular-nums text-[#F05A28] font-semibold w-20 text-right">{formatMXNCompact(data.received)}</span>
                      </div>
                    </div>
                    {/* Items */}
                    <div className="space-y-1">
                      {data.items.sort((a,b) => (a.scheduled_date||'').localeCompare(b.scheduled_date||'')).map(pp => {
                        const st = statusConfig[pp.payment_status] || statusConfig.projected;
                        return (
                          <div key={pp.id} className="flex items-center gap-2 text-[10px]">
                            <div className={`h-1.5 w-1.5 rounded-full shrink-0 ${pp.payment_status === 'received' ? 'bg-emerald-500' : pp.payment_status === 'overdue' ? 'bg-red-500' : pp.payment_status === 'partial' ? 'bg-amber-500' : 'bg-slate-300'}`} />
                            <span className="text-slate-500 truncate flex-1">Lt {pp.prop_lot} Mz {pp.prop_block}</span>
                            <span className="tabular-nums font-medium text-slate-700">{formatMXNCompact(pp.scheduled_amount)}</span>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>

      {/* ── EDIT MODAL (reuses abono logic) ── */}
      {editTarget && (
        <PaymentEditModal
          payment={editTarget}
          propName={`Lt ${editTarget.prop_lot} Mz ${editTarget.prop_block} — ${editTarget.prop_name}`}
          onClose={() => { setEditTarget(null); load(); }}
          onSaved={() => { setEditTarget(null); load(); }}
          onAbonoChanged={load}
        />
      )}

      {reminderTarget && (
        <ReminderModal
          payment={reminderTarget}
          onClose={() => setReminderTarget(null)}
          onSent={() => { setReminderTarget(null); load(); }}
        />
      )}
    </div>
  );
}


/* ══════════════════════════════════════════════
   PAYMENT EDIT MODAL (with abonos)
   ══════════════════════════════════════════════ */
function PaymentEditModal({ payment, propName, onClose, onSaved, onAbonoChanged }) {
  const [abonos, setAbonos] = useState([]);
  const [abonoForm, setAbonoForm] = useState({ date: '', amount: '' });
  const [editingAbono, setEditingAbono] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetchPaymentAbonos(payment.id).then(setAbonos).catch(() => setAbonos([]));
  }, [payment.id]);

  const handleAddAbono = async () => {
    if (!abonoForm.date || !abonoForm.amount) return;
    setBusy(true);
    try {
      await createPaymentAbono({ payment_id: payment.id, date: abonoForm.date, amount: parseFloat(abonoForm.amount) });
      setAbonoForm({ date: '', amount: '' });
      const fresh = await fetchPaymentAbonos(payment.id);
      setAbonos(fresh);
      toast.success('Abono registrado');
      if (onAbonoChanged) onAbonoChanged();
    } catch { toast.error('Error'); }
    finally { setBusy(false); }
  };

  const handleSaveAbono = async (id, updates) => {
    try {
      await updatePaymentAbono(id, updates);
      setAbonos(await fetchPaymentAbonos(payment.id));
      setEditingAbono(null);
      toast.success('Abono actualizado');
      if (onAbonoChanged) onAbonoChanged();
    } catch { toast.error('Error'); }
  };

  const handleDeleteAbono = async (id) => {
    try {
      await deletePaymentAbono(id);
      setAbonos(await fetchPaymentAbonos(payment.id));
      toast.success('Abono eliminado');
      if (onAbonoChanged) onAbonoChanged();
    } catch { toast.error('Error'); }
  };

  const handleClearAll = async () => {
    setBusy(true);
    try {
      await revertPaymentToProjected(payment.id);
      setAbonos([]);
      toast.success('Cobros limpiados');
      onSaved();
    } catch { toast.error('Error'); }
    finally { setBusy(false); }
  };

  const sched = payment.scheduled_amount || 0;
  const totalAbonos = abonos.reduce((s, a) => s + (a.amount || 0), 0);
  const pendiente = sched - totalAbonos;

  return (
    <Dialog open={true} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="payment-edit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-[#F05A28]" /> Pago — {typeLabel[payment.payment_type] || payment.payment_type}
          </DialogTitle>
          <p className="text-xs text-slate-400">{propName}</p>
        </DialogHeader>
        <div className="space-y-5 py-2">
          {/* Programado — solo lectura */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Programado</p>
              <span className="text-[9px] text-slate-400 italic">Definido en Viviendas</span>
            </div>
            <div className="bg-slate-50 rounded-lg p-3 text-xs space-y-1.5">
              <div className="flex justify-between"><span className="text-slate-500">Tipo</span><Badge variant="outline" className={`${typeBadge[payment.payment_type] || ''} text-[9px]`}>{typeLabel[payment.payment_type] || payment.payment_type}</Badge></div>
              <div className="flex justify-between"><span className="text-slate-500">Fecha programada</span><span className="tabular-nums font-medium">{formatDate(payment.scheduled_date)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Monto programado</span><span className="tabular-nums font-bold">{formatMXN(sched)}</span></div>
            </div>
          </div>

          {/* Abonos */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-[#F05A28]">Cobros Realizados</p>
              {abonos.length > 0 && (
                <div className="flex items-center gap-2 text-[10px] tabular-nums">
                  <span className="text-[#F05A28] font-bold">{formatMXN(totalAbonos)}</span>
                  {pendiente > 1 && <span className="text-amber-600">/ Pend: {formatMXN(pendiente)}</span>}
                  {pendiente <= 0 && <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[8px]">Completo</Badge>}
                </div>
              )}
            </div>
            {abonos.length > 0 ? (
              <div className="space-y-1.5 mb-3">
                {abonos.map((a, idx) => (
                  <div key={a.id} className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-2 group">
                    {editingAbono === a.id ? (
                      <EditAbonoInline abono={a} onSave={u => handleSaveAbono(a.id, u)} onCancel={() => setEditingAbono(null)} />
                    ) : (
                      <>
                        <span className="text-[10px] text-slate-400 w-4">{idx + 1}.</span>
                        <span className="text-[11px] tabular-nums text-slate-600 flex-1">{formatDate(a.date)}</span>
                        <span className="text-[11px] tabular-nums font-bold text-[#F05A28]">{formatMXN(a.amount)}</span>
                        <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Button variant="ghost" size="icon" className="h-5 w-5 text-slate-300 hover:text-slate-600" onClick={() => setEditingAbono(a.id)}><Pencil className="h-2.5 w-2.5" /></Button>
                          <Button variant="ghost" size="icon" className="h-5 w-5 text-slate-300 hover:text-red-500" onClick={() => handleDeleteAbono(a.id)}><Trash2 className="h-2.5 w-2.5" /></Button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[10px] text-slate-400 mb-3">Sin cobros registrados.</p>
            )}
            {/* Add abono */}
            <div className="bg-slate-50/80 rounded-lg p-3 border border-dashed border-slate-200">
              <p className="text-[10px] font-medium text-slate-500 mb-2"><Plus className="h-3 w-3 inline mr-1 text-[#F05A28]" />Nuevo cobro</p>
              <div className="grid grid-cols-[1fr_1fr_auto] gap-2">
                <div><label className="text-[10px] text-[#F05A28] mb-1 block">Fecha</label><Input type="date" className="h-8 text-xs" value={abonoForm.date} onChange={e => setAbonoForm(f => ({ ...f, date: e.target.value }))} /></div>
                <div><label className="text-[10px] text-[#F05A28] mb-1 block">Monto</label><Input type="number" step="0.01" className="h-8 text-xs" placeholder="0" value={abonoForm.amount} onChange={e => setAbonoForm(f => ({ ...f, amount: e.target.value }))} /></div>
                <div className="flex items-end"><Button size="sm" className="h-8 px-3 text-[10px] bg-emerald-600 hover:bg-emerald-700" onClick={handleAddAbono} disabled={busy || !abonoForm.date || !abonoForm.amount}><Plus className="h-3 w-3" /></Button></div>
              </div>
            </div>
          </div>
        </div>
        <DialogFooter className="flex-col sm:flex-row gap-2">
          {abonos.length > 0 && (
            <Button variant="ghost" size="sm" className="text-[11px] text-slate-400 hover:text-red-500 mr-auto" onClick={handleClearAll} disabled={busy}>Limpiar cobros</Button>
          )}
          <Button variant="outline" onClick={onClose}>Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditAbonoInline({ abono, onSave, onCancel }) {
  const [f, setF] = useState({ date: abono.date || '', amount: abono.amount || '' });
  return (
    <div className="flex items-center gap-2 flex-1">
      <Input type="date" className="h-7 text-xs flex-1" value={f.date} onChange={e => setF(p => ({ ...p, date: e.target.value }))} />
      <Input type="number" step="0.01" className="h-7 text-xs w-24" value={f.amount} onChange={e => setF(p => ({ ...p, amount: e.target.value }))} />
      <Button size="sm" className="h-7 px-2 text-[10px]" onClick={() => onSave({ date: f.date, amount: parseFloat(f.amount) || 0 })}><Check className="h-3 w-3" /></Button>
      <Button variant="ghost" size="sm" className="h-7 px-2 text-[10px]" onClick={onCancel}><XIcon className="h-3 w-3" /></Button>
    </div>
  );
}

/* ══════════════════════════════════════════════
   REMINDER MODAL
   ══════════════════════════════════════════════ */
function ReminderModal({ payment, onClose, onSent }) {
  const [channel, setChannel] = useState('whatsapp');
  const [tone, setTone] = useState('formal');
  const [isTest, setIsTest] = useState(false);
  const [recipient, setRecipient] = useState('');
  const [testRecipient, setTestRecipient] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    const init = async () => {
      try {
        const [preview, hist] = await Promise.all([
          fetchNotificationPreview(channel, payment.id),
          fetchNotificationHistory(payment.id),
        ]);
        setRecipient(preview.recipient || '');
        setMessage(preview.message || '');
        setHistory(hist);
      } catch {}
      setLoading(false);
    };
    init();
  }, [payment.id]);

  const handleChannelChange = async (ch) => {
    setChannel(ch);
    try {
      const preview = await fetchNotificationPreview(ch, payment.id);
      setRecipient(preview.recipient || '');
      setMessage(preview.message || '');
      setSubject('');
    } catch {}
  };

  const handleAIGenerate = async () => {
    setGenerating(true);
    try {
      const result = await generateAIMessage({ payment_id: payment.id, tone, channel });
      if (result.error) { toast.error('Error de IA: ' + result.error); return; }
      if (channel === 'whatsapp') {
        setMessage(result.whatsapp || result.body || '');
      } else {
        setSubject(result.subject || '');
        setMessage(result.body || '');
      }
      toast.success('Mensaje generado por IA');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Error al generar'); }
    finally { setGenerating(false); }
  };

  const handleSend = async () => {
    const dest = isTest ? testRecipient : recipient;
    if (!dest) { toast.error(channel === 'email' ? 'Falta email' : 'Falta teléfono'); return; }
    setSending(true);
    try {
      const fullMsg = channel === 'email' && subject ? `Asunto: ${subject}\n\n${message}` : message;
      const result = await sendNotification({ payment_id: payment.id, channel, recipient: dest, message: fullMsg, is_test: isTest });
      if (result.status === 'sent') {
        toast.success(isTest ? `Prueba enviada por ${channel === 'email' ? 'correo' : 'WhatsApp'}` : `Recordatorio enviado por ${channel === 'email' ? 'correo' : 'WhatsApp'}`);
      } else if (result.status === 'pending_config') {
        toast.warning(result.error || 'Canal no configurado');
      } else {
        toast.error(`Error al enviar: ${result.error || 'desconocido'}`);
      }
      if (!isTest) onSent();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Error al enviar'); }
    finally { setSending(false); }
  };

  const TONES = [
    { value: 'cordial', label: 'Cordial', desc: 'Amable y cercano' },
    { value: 'formal', label: 'Formal', desc: 'Profesional' },
    { value: 'firme', label: 'Firme', desc: 'Directo y serio' },
    { value: 'urgente', label: 'Urgente', desc: 'Crítico' },
  ];

  return (
    <Dialog open={true} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="reminder-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Bell className="h-5 w-5 text-[#F05A28]" /> Enviar Recordatorio</DialogTitle>
          <p className="text-xs text-slate-400">Lt {payment.prop_lot} Mz {payment.prop_block} — {payment.prop_name}</p>
        </DialogHeader>

        {loading ? <Skeleton className="h-48" /> : (
          <div className="space-y-4 py-2">
            {/* Channel + Tone (always visible) */}
            <div className={`grid ${channel === 'email' && !isTest ? 'grid-cols-2' : 'grid-cols-1'} gap-3`}>
              <div>
                <label className="text-[10px] font-medium text-slate-500 mb-1.5 block">Canal</label>
                <div className="flex gap-1.5">
                  <Button size="sm" variant={channel === 'whatsapp' ? 'default' : 'outline'} className="flex-1 h-8 text-[11px]" onClick={() => handleChannelChange('whatsapp')}>WhatsApp</Button>
                  <Button size="sm" variant={channel === 'email' ? 'default' : 'outline'} className="flex-1 h-8 text-[11px]" onClick={() => handleChannelChange('email')}>Correo</Button>
                </div>
              </div>
              {channel === 'email' && !isTest && (
                <div>
                  <label className="text-[10px] font-medium text-slate-500 mb-1.5 block">Tono</label>
                  <div className="flex gap-1">
                    {TONES.map(t => (
                      <Button key={t.value} size="sm" variant={tone === t.value ? 'default' : 'outline'} className="flex-1 h-8 text-[10px] px-1.5" onClick={() => setTone(t.value)} title={t.desc}>{t.label}</Button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* AI Generate button — solo para email real */}
            {channel === 'email' && !isTest && (
              <Button variant="outline" className="w-full h-9 text-xs border-dashed border-[#F05A28]/30 text-[#F05A28] hover:bg-[#F05A28]/5" onClick={handleAIGenerate} disabled={generating} data-testid="ai-generate-btn">
                <Sparkles className="h-3.5 w-3.5 mr-1.5" />
                {generating ? 'Generando...' : 'Generar mensaje con IA'}
              </Button>
            )}

            {/* Recipient — real mode */}
            {!isTest && (
              <div>
                <label className="text-[10px] font-medium text-slate-500 mb-1 block">{channel === 'email' ? 'Correo del destinatario' : 'Teléfono del destinatario (10 dígitos)'}</label>
                <Input value={recipient} onChange={e => setRecipient(e.target.value)} placeholder={channel === 'email' ? 'correo@ejemplo.com' : '10 dígitos ej: 5551020707'} className="h-8 text-sm" />
                {recipient ? (
                  <p className="text-[10px] text-emerald-600 mt-1 flex items-center gap-1"><CheckCircle2 className="h-3 w-3" />Tomado de Viviendas · Lt {payment.prop_lot} Mz {payment.prop_block}</p>
                ) : (
                  <p className="text-[10px] text-amber-600 mt-1 flex items-center gap-1"><AlertTriangle className="h-3 w-3" />Sin {channel === 'email' ? 'email' : 'teléfono'} registrado en Viviendas. Ingresa manualmente o actualiza la vivienda.</p>
                )}
              </div>
            )}

            {/* Subject (email only) */}
            {channel === 'email' && (
              <div>
                <label className="text-[10px] font-medium text-slate-500 mb-1 block">Asunto</label>
                <Input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Asunto del correo" className="h-8 text-sm" />
              </div>
            )}

            {/* Message — editable para email, preview para WhatsApp */}
            {channel === 'email' ? (
              <div>
                <label className="text-[10px] font-medium text-slate-500 mb-1 block">Mensaje</label>
                <textarea className="w-full min-h-[120px] text-sm border rounded-lg px-3 py-2 text-slate-700 resize-none focus:ring-1 focus:ring-[#F05A28] focus:outline-none" value={message} onChange={e => setMessage(e.target.value)} />
                <p className="text-[9px] text-slate-400 mt-0.5 text-right">{message.length} caracteres</p>
              </div>
            ) : (
              <div>
                <label className="text-[10px] font-medium text-slate-500 mb-1 block">Plantilla WhatsApp</label>
                <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-sm text-slate-600 leading-relaxed">
                  <p>Estimado(a) <strong>{payment.prop_name || 'Cliente'}</strong>, por medio del presente le recordamos que tiene un pago pendiente por la cantidad de <strong>{formatMXN(payment.scheduled_amount)}</strong> MXN con fecha de vencimiento <strong>{payment.scheduled_date ? (() => { const p = payment.scheduled_date.split('-'); return `${p[2]}/${p[1]}/${p[0]}`; })() : '—'}</strong>, correspondiente a su vivienda Lt {payment.prop_lot} Mz {payment.prop_block} en nuestro desarrollo.</p>
                  <p className="mt-2">Le solicitamos amablemente realizar su pago a la brevedad para mantener al corriente su calendario de pagos y no afectar el cronograma de construcción de su vivienda. En caso de haber realizado su pago, favor de hacer caso omiso a este mensaje.</p>
                  <p className="mt-2">Si tiene alguna duda o requiere asistencia, no dude en contactar a su ejecutivo de cuenta.</p>
                  <p className="mt-2">Atentamente, Administración Quru.</p>
                </div>
                <p className="text-[9px] text-slate-400 mt-1">Esta plantilla fue aprobada por Meta y no puede editarse.</p>
              </div>
            )}

            {/* Payment context — only real mode */}
            {!isTest && (
              <div className="bg-slate-50 rounded-lg p-3 text-xs space-y-1">
                <div className="flex justify-between"><span className="text-slate-500">Monto</span><span className="font-bold tabular-nums">{formatMXN(payment.scheduled_amount)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Fecha programada</span><span className="tabular-nums">{formatDate(payment.scheduled_date)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Recordatorios previos</span><span className="font-semibold">{payment.reminder_count || 0}</span></div>
              </div>
            )}

            {/* Test mode: recipient input */}
            {isTest && (
              <div className="bg-amber-50/50 border border-amber-200 rounded-lg p-3 space-y-3">
                <div className="flex items-center gap-2 text-[10px] text-amber-700">
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                  <span className="font-medium">Modo prueba — no afecta contadores ni métricas</span>
                </div>
                <div>
                  <label className="text-[10px] font-medium text-amber-700 mb-1 block">{channel === 'email' ? 'Correo de prueba' : 'Teléfono de prueba (10 dígitos)'}</label>
                  <Input value={testRecipient} onChange={e => setTestRecipient(e.target.value)} placeholder={channel === 'email' ? 'tu@correo.com' : '10 dígitos ej: 5551020707'} className="h-8 text-sm bg-white" />
                </div>
              </div>
            )}

            {/* History — only real mode */}
            {!isTest && history.length > 0 && (
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-2">Historial ({history.length})</p>
                <div className="space-y-1 max-h-[100px] overflow-y-auto">
                  {history.map((h, i) => (
                    <div key={i} className="flex items-center gap-2 text-[10px] bg-slate-50 rounded px-2.5 py-1.5">
                      <Badge variant="outline" className={`text-[8px] ${h.channel === 'whatsapp' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-blue-50 text-blue-700 border-blue-200'}`}>{h.channel === 'whatsapp' ? 'WA' : 'Email'}</Badge>
                      {h.is_test && <Badge variant="outline" className="text-[7px] bg-amber-50 text-amber-600 border-amber-200">PRUEBA</Badge>}
                      <span className="text-slate-500 tabular-nums">{new Date(h.timestamp).toLocaleDateString('es-MX', { day: '2-digit', month: 'short' })}</span>
                      <span className="text-slate-600 truncate flex-1">{h.recipient}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <button
            onClick={() => setIsTest(!isTest)}
            className={`text-[10px] mr-auto px-2 py-1 rounded transition-colors ${isTest ? 'text-amber-600 hover:text-amber-800' : 'text-slate-400 hover:text-slate-600'}`}
          >
            {isTest ? 'Cambiar a envío real' : 'Enviar prueba'}
          </button>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button
            onClick={handleSend}
            disabled={sending || (isTest ? !testRecipient : (!recipient || (channel === 'email' && !message)))}
            className={isTest ? 'bg-amber-500 hover:bg-amber-600' : ''}
            data-testid="reminder-send"
          >
            <Send className="h-4 w-4 mr-1.5" />
            {sending ? 'Enviando...' : isTest ? 'Enviar Prueba' : `Enviar por ${channel === 'email' ? 'Correo' : 'WhatsApp'}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* ══════════════════════════════════════════════
   REMINDER QUEUE VIEW
   ══════════════════════════════════════════════ */
function ReminderQueueView() {
  const [queue, setQueue] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [filterStatus, setFilterStatus] = useState('pending');

  const load = useCallback(async () => {
    try {
      const [q, s] = await Promise.all([
        fetchReminderQueue({ status: filterStatus !== 'all' ? filterStatus : undefined, limit: 100 }),
        fetchQueueSummary(),
      ]);
      setQueue(q);
      setSummary(s);
    } catch { toast.error('Error al cargar cola'); }
    finally { setLoading(false); }
  }, [filterStatus]);

  useEffect(() => { load(); }, [load]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const result = await generateReminders();
      toast.success(result.message);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Error'); }
    finally { setGenerating(false); }
  };

  const handleApprove = async (id) => { try { await approveQueueItem(id); toast.success('Aprobado'); load(); } catch { toast.error('Error'); } };
  const handleSend = async (id) => { try { await sendQueueItem(id); toast.success('Enviado'); load(); } catch (err) { toast.error(err?.response?.data?.detail || 'Error'); } };
  const handleDiscard = async (id) => { try { await discardQueueItem(id); toast.success('Descartado'); load(); } catch { toast.error('Error'); } };

  const statusBadge = (s) => {
    const cfg = { pending: 'bg-amber-50 text-amber-700 border-amber-200', approved: 'bg-blue-50 text-blue-700 border-blue-200', sent: 'bg-emerald-50 text-emerald-700 border-emerald-200', discarded: 'bg-slate-50 text-slate-400 border-slate-200', failed: 'bg-red-50 text-red-600 border-red-200' };
    const labels = { pending: 'Pendiente', approved: 'Aprobado', sent: 'Enviado', discarded: 'Descartado', failed: 'Fallido' };
    return <Badge variant="outline" className={`${cfg[s] || cfg.pending} text-[9px]`}>{labels[s] || s}</Badge>;
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[150px] h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="pending">Pendientes ({summary.pending || 0})</SelectItem>
              <SelectItem value="approved">Aprobados ({summary.approved || 0})</SelectItem>
              <SelectItem value="sent">Enviados ({summary.sent || 0})</SelectItem>
              <SelectItem value="discarded">Descartados ({summary.discarded || 0})</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button onClick={handleGenerate} disabled={generating} data-testid="generate-queue-btn">
          <Sparkles className="h-4 w-4 mr-1.5" />
          {generating ? 'Generando...' : 'Generar Recordatorios'}
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-4 gap-3">
        <Card className="rounded-xl border bg-white border-amber-200"><CardContent className="p-3 text-center"><p className="text-[9px] uppercase text-slate-400">Pendientes</p><p className="text-lg font-bold text-amber-600">{summary.pending || 0}</p></CardContent></Card>
        <Card className="rounded-xl border bg-white border-blue-200"><CardContent className="p-3 text-center"><p className="text-[9px] uppercase text-slate-400">Aprobados</p><p className="text-lg font-bold text-blue-600">{summary.approved || 0}</p></CardContent></Card>
        <Card className="rounded-xl border bg-white border-emerald-200"><CardContent className="p-3 text-center"><p className="text-[9px] uppercase text-slate-400">Enviados</p><p className="text-lg font-bold text-emerald-600">{summary.sent || 0}</p></CardContent></Card>
        <Card className="rounded-xl border bg-white"><CardContent className="p-3 text-center"><p className="text-[9px] uppercase text-slate-400">Total</p><p className="text-lg font-bold text-slate-700">{summary.total || 0}</p></CardContent></Card>
      </div>

      {/* Table */}
      {loading ? <Skeleton className="h-[300px] rounded-xl" /> : queue.length === 0 ? (
        <Card className="rounded-xl border bg-white"><CardContent className="py-12 text-center"><Bell className="h-10 w-10 text-slate-200 mx-auto mb-3" /><p className="text-sm text-slate-400">Sin recordatorios en cola</p><p className="text-xs text-slate-300 mt-1">Usa "Generar Recordatorios" para escanear pagos pendientes</p></CardContent></Card>
      ) : (
        <Card className="rounded-xl border bg-white overflow-hidden">
          <div className="overflow-auto max-h-[400px]">
            <Table data-testid="queue-table">
              <TableHeader>
                <TableRow className="bg-slate-50 sticky top-0 z-10">
                  <TableHead className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold py-2.5 px-2.5">Cliente</TableHead>
                  <TableHead className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold py-2.5 px-2.5">Vivienda</TableHead>
                  <TableHead className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold py-2.5 px-2.5">Canal</TableHead>
                  <TableHead className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold py-2.5 px-2.5">Regla</TableHead>
                  <TableHead className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold py-2.5 px-2.5 text-right">Monto</TableHead>
                  <TableHead className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold py-2.5 px-2.5">Estatus</TableHead>
                  <TableHead className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold py-2.5 px-2.5 w-36">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {queue.map(item => (
                  <TableRow key={item.id} className="group">
                    <TableCell className="py-2 px-2.5 text-[11px] text-slate-700 font-medium truncate max-w-[130px]">{item.client_name || '—'}</TableCell>
                    <TableCell className="py-2 px-2.5 text-[11px] font-semibold whitespace-nowrap">Lt {item.client_lot} Mz {item.client_block}</TableCell>
                    <TableCell className="py-2 px-2.5"><Badge variant="outline" className={`text-[9px] ${item.channel === 'whatsapp' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-blue-50 text-blue-700 border-blue-200'}`}>{item.channel === 'whatsapp' ? 'WA' : 'Email'}</Badge></TableCell>
                    <TableCell className="py-2 px-2.5 text-[10px] text-slate-500 max-w-[150px] truncate" title={item.rule_label}>{item.rule_label}</TableCell>
                    <TableCell className="py-2 px-2.5 text-right text-[11px] tabular-nums font-medium">{formatMXN(item.scheduled_amount)}</TableCell>
                    <TableCell className="py-2 px-2.5">{statusBadge(item.status)}</TableCell>
                    <TableCell className="py-2 px-2.5">
                      <div className="flex items-center gap-1">
                        {item.status === 'pending' && (
                          <>
                            <Button size="sm" className="h-6 px-2 text-[10px] bg-blue-600 hover:bg-blue-700" onClick={() => handleApprove(item.id)}>Aprobar</Button>
                            <Button size="sm" variant="ghost" className="h-6 px-1.5 text-[10px] text-slate-400 hover:text-red-500" onClick={() => handleDiscard(item.id)}>Descartar</Button>
                          </>
                        )}
                        {item.status === 'approved' && (
                          <Button size="sm" className="h-6 px-2 text-[10px] bg-emerald-600 hover:bg-emerald-700" onClick={() => handleSend(item.id)}><Send className="h-3 w-3 mr-1" />Enviar</Button>
                        )}
                        {item.status === 'sent' && <span className="text-[10px] text-emerald-500">Enviado {item.sent_at?.slice(0, 10)}</span>}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}
    </div>
  );
}


function SortableHead({ col, label, sortCol, sortDir, onSort, accent }) {
  const active = sortCol === col;
  const Icon = active ? (sortDir === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
  return (
    <TableHead
      className={`text-[9px] uppercase tracking-widest font-semibold py-2.5 px-2.5 cursor-pointer select-none hover:bg-slate-100/50 transition-colors ${accent ? 'text-[#F05A28]' : active ? 'text-slate-700' : 'text-slate-400'}`}
      onClick={() => onSort(col)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <Icon className={`h-3 w-3 ${active ? 'text-[#F05A28]' : 'text-slate-300'}`} />
      </span>
    </TableHead>
  );
}

